import { useEffect, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { ExternalLink, Info, KeyRound, X } from 'lucide-react';
import { SocialPlatform, type OAuthRedirectUris } from '@mg/shared';
import { cn } from '../lib/cn';
import { PLATFORM_META } from '../features/social/platforms';
import { GuideByoaNote } from '../features/social/GuideByoaNote';
import { useOAuthRedirectUris } from '../features/social/useOAuthRedirectUris';
import { NotFoundPage } from './NotFoundPage';

interface GuideContent {
  platform: SocialPlatform;
  intro: string;
  steps: { title: string; body: ReactNode }[];
  notes: ReactNode[];
  /** False for a page that sets up no app of its own (Instagram: it uses the Facebook app). Hides the secret box and the "You own this app" note. */
  setsUpApp?: boolean;
}

function link(href: string, label: string) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 font-medium text-link hover:text-action-hover"
    >
      {label} <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </a>
  );
}

/** The callback URL to register, as the server sends it: "Loading…" until it has answered. */
function RedirectBlock({ uri }: { uri: keyof OAuthRedirectUris }) {
  const url = useOAuthRedirectUris()?.[uri] ?? null;
  return (
    <code
      className={cn(
        'mt-2 block break-all rounded-lg bg-chip px-3 py-2 text-xs',
        url ? 'text-ink' : 'text-ink-faint',
      )}
    >
      {url ?? 'Loading…'}
    </code>
  );
}

const CONTENT: Record<string, GuideContent> = {
  instagram: {
    platform: SocialPlatform.INSTAGRAM,
    intro:
      'New Instagram connections sign in through Facebook: Smorgas Social uses the Facebook app your organization added in Site Integrations, so there is no separate Instagram app to set up.',
    // It sets up no app of its own (it signs in with the Facebook app), so there is no secret to
    // keep and no callback URL to register: the page asks the server nothing.
    setsUpApp: false,
    steps: [
      {
        title: 'Set up the Facebook app',
        body: (
          <>
            A Tenant Admin follows {link('/guide/facebook-app', 'Get your Facebook App ID & Secret')},
            including its Instagram step, and adds the App ID and App Secret under Site Integrations →
            Social Media Integrations → <span className="font-semibold">Facebook</span>.
          </>
        ),
      },
      {
        title: 'Make your Instagram account a professional account',
        body: (
          <>
            Your Instagram account must be a <span className="font-semibold">professional</span>{' '}
            account (Business or Creator) connected to a{' '}
            <span className="font-semibold">Facebook Page</span> that belongs to a{' '}
            <span className="font-semibold">Meta Business account</span>.
          </>
        ),
      },
      {
        title: 'Connect it',
        body: (
          <>
            On the Connections page, choose <span className="font-semibold">Connect Instagram</span>,
            then <span className="font-semibold">Continue to Facebook</span>. Approve the permissions
            and choose the Instagram account.
          </>
        ),
      },
    ],
    notes: [
      <>Personal Instagram accounts can&apos;t be connected.</>,
      <>
        An Instagram account connected earlier with Instagram Login keeps working with its own
        Instagram app. To use the Messages inbox with it, that app needs the{' '}
        <span className="font-semibold">instagram_business_manage_messages</span> and{' '}
        <span className="font-semibold">instagram_business_manage_comments</span> permissions under{' '}
        <span className="font-semibold">API setup with Instagram login</span>; then choose{' '}
        <span className="font-semibold">Enable inbox</span> on the Connections page.
      </>,
    ],
  },
  threads: {
    platform: SocialPlatform.THREADS,
    intro:
      'Smorgas Social connects to Threads through a Meta app your organization owns: a Tenant Admin sets it up once and adds its two values in Site Integrations. Follow along and copy them at the end.',
    steps: [
      {
        title: 'Open Meta for Developers',
        body: <>Go to {link('https://developers.facebook.com/apps', 'developers.facebook.com/apps')} and create a new app or open an existing one.</>,
      },
      {
        title: 'Add the Threads use case',
        body: (
          <>
            Open <span className="font-semibold">Use cases</span> → choose{' '}
            <span className="font-semibold">Access the Threads API</span> →{' '}
            <span className="font-semibold">Customize</span>, and add these permissions:{' '}
            <span className="font-semibold">threads_basic</span>,{' '}
            <span className="font-semibold">threads_content_publish</span>,{' '}
            <span className="font-semibold">threads_read_replies</span> and{' '}
            <span className="font-semibold">threads_manage_insights</span>.
          </>
        ),
      },
      {
        title: 'Add yourself as a tester',
        body: (
          <>
            Under <span className="font-semibold">App roles → Roles</span>, add your Threads account
            as a Tester or Admin, then accept the invite in your Threads app settings (new apps
            start in Development mode).
          </>
        ),
      },
      {
        title: 'Register the redirect URL',
        body: (
          <>
            Go to <span className="font-semibold">Use cases → Threads API → Settings</span> and add
            this exact value under <span className="font-semibold">Redirect Callback URLs</span>,
            then save:
            <RedirectBlock uri="threads" />
          </>
        ),
      },
      {
        title: 'Copy your Threads App ID & Secret',
        body: (
          <>
            On the same <span className="font-semibold">Settings</span> page, copy the{' '}
            <span className="font-semibold">Threads app ID</span> and{' '}
            <span className="font-semibold">Threads app secret</span> (not the App ID under App
            settings → Basic), then paste both into Site Integrations → Social Media Integrations →
            Threads in Smorgas Social.
          </>
        ),
      },
    ],
    notes: [
      <>
        While your app is in Development mode, only people added as{' '}
        <span className="font-semibold">testers</span> can connect.
      </>,
      <>
        Mentions and thread publishing are optional - turn them on from the Connections page after
        you connect; each needs one more permission (
        <span className="font-semibold">threads_manage_mentions</span>,{' '}
        <span className="font-semibold">threads_manage_replies</span>).
      </>,
      <>
        The redirect URL must match <span className="font-semibold">exactly</span> — copy it from
        this page or from Site Integrations.
      </>,
    ],
  },
  x: {
    platform: SocialPlatform.X,
    intro:
      'Smorgas Social connects to X through an X app your organization owns, using OAuth 2.0: a Tenant Admin sets it up once and adds its two values in Site Integrations. Follow along and copy them at the end.',
    steps: [
      {
        title: 'Open the X Developer Console',
        body: (
          <>
            Go to {link('https://console.x.com', 'console.x.com')} and sign up for a developer
            account if you don&apos;t have one. X&apos;s API is pay-per-use: connecting is free, but
            every post and every read (Messages, analytics) is billed to your X app, so add a
            payment method, buy credits and set a spend cap in the Developer Console.
          </>
        ),
      },
      {
        title: 'Create a Project and an App',
        body: (
          <>
            In the dashboard, create a <span className="font-semibold">Project</span>, then create
            an <span className="font-semibold">App</span> inside it.
          </>
        ),
      },
      {
        title: 'Set up User authentication (OAuth 2.0)',
        body: (
          <>
            Open your app → <span className="font-semibold">User authentication settings</span> →{' '}
            <span className="font-semibold">Set up</span>. Choose{' '}
            <span className="font-semibold">OAuth 2.0</span>, type of app{' '}
            <span className="font-semibold">Web App (Confidential client)</span>, and app permissions{' '}
            <span className="font-semibold">Read and write</span>.
          </>
        ),
      },
      {
        title: 'Register the Callback URL',
        body: (
          <>
            In the same settings, add this exact value under{' '}
            <span className="font-semibold">Callback URI / Redirect URL</span> (set any valid{' '}
            <span className="font-semibold">Website URL</span> too), then save:
            <RedirectBlock uri="x" />
          </>
        ),
      },
      {
        title: 'Copy your Client ID & Client Secret',
        body: (
          <>
            Open <span className="font-semibold">Keys and tokens</span> and copy the{' '}
            <span className="font-semibold">OAuth 2.0 Client ID</span> and{' '}
            <span className="font-semibold">Client Secret</span>, then paste both into
            Site Integrations → Social Media Integrations → X / Twitter in Smorgas Social.
          </>
        ),
      },
    ],
    notes: [
      <>
        Use the <span className="font-semibold">OAuth 2.0 Client ID &amp; Secret</span> — not the
        older API Key/Secret.
      </>,
      <>
        The redirect URL must match <span className="font-semibold">exactly</span> — copy it from
        this page or from Site Integrations.
      </>,
    ],
  },
};

/**
 * Standalone, self-contained "Get your <platform> app credentials" guide, opened
 * in a new tab from Site Integrations (Threads, X); the Instagram page explains
 * the Facebook route. Renders OUTSIDE the agency nav shell and needs no auth.
 */
export function ConnectAppGuide() {
  const { platform: slug } = useParams<{ platform: string }>();
  const content = slug ? CONTENT[slug.toLowerCase()] : undefined;

  useEffect(() => {
    if (!content) return;
    const prev = document.title;
    document.title = `${PLATFORM_META[content.platform].label} setup · Smorgas Social`;
    return () => {
      document.title = prev;
    };
  }, [content]);

  if (!content) {
    return <NotFoundPage />;
  }

  const meta = PLATFORM_META[content.platform];
  const { Icon } = meta;
  const setsUpApp = content.setsUpApp !== false;

  return (
    // h-full + overflow-y-auto: these guides are STANDALONE routes (no AppShell), and
    // body/#root are locked to overflow:hidden so the app's inner regions own scrolling.
    // Without its own scroll region a guide taller than the viewport is simply clipped.
    <div className="h-full overflow-y-auto bg-page text-ink">
      <div className="mx-auto max-w-2xl px-5 py-10 sm:py-14">
        {/* Branding. The page follows the theme, so the logo does too: dark lettering on the light
            page, white on the dark one. The files have no size of their own, hence width and height,
            drawn in rem by the classes so the logo scales with the page (index.css). */}
        <div className="mb-8 flex items-center">
          <img
            src="/smorgas-social-logo-light.svg"
            alt="Smorgas Social"
            width={137}
            height={21}
            className="block h-[1.3125rem] w-[8.5625rem] dark:hidden"
          />
          <img
            src="/smorgas-social-logo.svg"
            alt="Smorgas Social"
            width={137}
            height={21}
            className="hidden h-[1.3125rem] w-[8.5625rem] dark:block"
          />
        </div>

        <div className="rounded-2xl border border-line bg-card p-6 shadow-card sm:p-8">
          <div className="mb-6 flex items-start gap-3">
            <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${meta.tile}`}>
              <Icon className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-ink">
                Connect your {meta.label} account
              </h1>
              <p className="mt-1 text-sm text-ink-muted">{content.intro}</p>
            </div>
          </div>

          <ol className="space-y-5">
            {content.steps.map((step, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-tint text-sm font-bold text-tint-fg">
                  {i + 1}
                </span>
                <div className="pt-0.5">
                  <p className="text-sm font-semibold text-ink">{step.title}</p>
                  <div className="mt-1 text-sm leading-relaxed text-ink-label">{step.body}</div>
                </div>
              </li>
            ))}
          </ol>

          {setsUpApp && (
            <div className="mt-7 flex gap-3 rounded-xl border border-amber-100 dark:border-warning-dark/30 bg-amber-50/70 dark:bg-warning-dark-tint/70 px-4 py-3">
              <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-amber-500 dark:text-warning-dark" aria-hidden />
              <div className="text-sm text-amber-800/90 dark:text-warning-dark">
                <p className="font-semibold text-amber-900 dark:text-warning-dark">Keep your secret private</p>
                <p className="mt-1">
                  Treat the App Secret / Client Secret like a password. Smorgas Social keeps it on
                  its server, never shows it again and uses it only to complete sign-ins — don&apos;t
                  share it elsewhere.
                </p>
              </div>
            </div>
          )}

          <div
            className={cn(
              'flex gap-3 rounded-xl border border-line bg-tint/70 px-4 py-3',
              // The box above it is gone on a page that sets up no app: keep the steps' spacing.
              setsUpApp ? 'mt-4' : 'mt-7',
            )}
          >
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-tint-fg" aria-hidden />
            <div className="text-sm text-ink-strong/90">
              <p className="font-semibold text-ink">Good to know</p>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                {content.notes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            </div>
          </div>

          {setsUpApp && <GuideByoaNote />}
        </div>

        <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-sm text-ink-faint">
          <X className="h-4 w-4" aria-hidden /> You can close this tab and return to Smorgas Social
          when you&apos;re done.
        </p>
      </div>
    </div>
  );
}
